import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Form, Formik } from 'formik';
import { gql, useMutation } from '@apollo/client';
import * as yup from 'yup';
import { Button, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { useMultiProjectDashboardContext } from '../../contexts/MultiProjectDashboardContext';
import { NEW_DASHBOARD_ID } from '../../hooks/useDashboardState';
import { DeleteButton, ValidatedInput } from '../templates/template-components';
import { copyName, uniqueName } from '../../utils/utils';
import useMutateWithState from '../../hooks/useMutateWithState';
import { UpsertMultiProjectDashboardMutation } from '../../mutations';
import { captureEvent } from '../../utils/analytics';
import { showError } from '../../utils/toast';

const DeleteMultiProjectDashboardMutation = gql`
  mutation DeleteMultiProjectDashboard(
    $input: DeleteMultiProjectDashboardInput!
  ) {
    deleteMultiProjectDashboard(input: $input) {
      errors
      result
    }
  }
`;

const validationSchema = yup.object({
  name: yup.string().required('Required'),
});

const CreateDashboardViewModal = ({
  defaultName,
  dashboard,
  duplicate = false,
  hide,
}) => {
  const initialValues = {
    ...dashboard,
    name: defaultName,
  };
  const [createView] = useMutation(UpsertMultiProjectDashboardMutation);
  const navigate = useNavigate();
  const {
    dashboards: { clearDashboard },
  } = useMultiProjectDashboardContext();
  const [{ loading }, mutateAndSetState] = useMutateWithState();

  return (
    <Modal isOpen toggle={() => !loading && hide()} size="sm">
      <ModalHeader className="font-xl">
        {duplicate ? 'Duplicate' : 'Create'} View
      </ModalHeader>
      <ModalBody>
        <Formik
          initialValues={initialValues}
          onSubmit={async (values) => {
            // Save dashboard and dashboard farms
            const res = await mutateAndSetState(
              () =>
                createView({
                  variables: {
                    input: {
                      ...values,
                    },
                  },
                }),
              `View ${duplicate ? 'duplicated' : 'created'}!`,
            );
            const result = res?.data?.upsertMultiProjectDashboard?.result;
            // Get ID or display error
            // Navigate to new view
            if (result.id) {
              if (duplicate) {
                captureEvent('dashboard-view-duplicated');
                navigate(`../${result.id}`);
              } else {
                captureEvent('dashboard-view-created');
                // Only clear the new dashboard state when we create a dashboard from it
                // otherwise, we want to preserve the state in case the user navigates back to it
                clearDashboard(NEW_DASHBOARD_ID);
                navigate(result.id);
              }
              hide();
            }
          }}
          validationSchema={validationSchema}
        >
          <Form>
            <ValidatedInput name="name" label="Name" />
            <div className="d-flex justify-content-end">
              <Button
                type="button"
                color="secondary"
                size="sm"
                onClick={hide}
                disabled={loading}
                outline
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                size="sm"
                className="ml-2"
                disabled={loading}
              >
                Save
              </Button>
            </div>
          </Form>
        </Formik>
      </ModalBody>
    </Modal>
  );
};

const DashboardViewButtons = () => {
  const {
    dashboards: {
      dashboards,
      dashboardFarms,
      activeDashboardId,
      activeDashboard,
      removeDashboard,
    },
  } = useMultiProjectDashboardContext();

  const [deleteView] = useMutation(DeleteMultiProjectDashboardMutation);

  const [showCreateViewModal, setShowCreateViewModal] = useState(false);
  const [showDuplicateViewModal, setShowDuplicateViewModal] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  const navigate = useNavigate();

  return (
    <div className="d-flex align-items-center">
      {activeDashboardId === NEW_DASHBOARD_ID && dashboardFarms.length > 0 && (
        <>
          <Button
            type="button"
            onClick={() => setShowCreateViewModal(true)}
            outline
            size="sm"
          >
            Create View
          </Button>
          {showCreateViewModal && (
            <CreateDashboardViewModal
              defaultName={uniqueName(
                'New Dashboard',
                // filter because the NEW_DASHBOARD won't have a name
                dashboards.filter((d) => d.name),
              )}
              dashboard={{
                farmIds: dashboardFarms.map((farm) => farm.id),
                cropColorMap: activeDashboard?.cropColorMap,
              }}
              hide={() => setShowCreateViewModal(false)}
            />
          )}
        </>
      )}
      {activeDashboardId !== NEW_DASHBOARD_ID && (
        <>
          <Button
            type="button"
            onClick={() => setShowDuplicateViewModal(true)}
            size="sm"
            outline
            className="mr-2"
          >
            Duplicate View
          </Button>
          <DeleteButton
            buttonText="Delete View"
            disabled={deleteInProgress}
            onDelete={async () => {
              setDeleteInProgress(true);
              try {
                const res = await deleteView({
                  variables: {
                    input: {
                      id: activeDashboardId,
                      updatedAt: activeDashboard?.updatedAt,
                    },
                  },
                });
                if (res?.data?.deleteMultiProjectDashboard?.result) {
                  captureEvent('dashboard-view-deleted');
                  removeDashboard(activeDashboardId);
                  navigate('..');
                }
              } catch (e) {
                showError(e);
              } finally {
                setDeleteInProgress(false);
              }
            }}
            outline
            size="sm"
            // delete button should not be highlighted red in its default state
            color={undefined}
            // but should be red when active and asking the user to confirm the delete
            confirmDeleteButtonProps={{ color: 'danger' }}
          />
          {showDuplicateViewModal && (
            <CreateDashboardViewModal
              defaultName={copyName(
                activeDashboard?.name, // filter because the NEW_DASHBOARD won't have a name
                dashboards.filter((d) => d.name),
              )}
              dashboard={{
                farmIds: dashboardFarms.map((farm) => farm.id),
                cropColorMap: activeDashboard?.cropColorMap,
              }}
              duplicate
              hide={() => setShowDuplicateViewModal(false)}
            />
          )}
        </>
      )}
    </div>
  );
};

export default DashboardViewButtons;
